/**
 * Shared stepped front-first packing helpers (port of ContainerFiller/packing.py).
 * Options (minStepWidth, diagonalMode) let GRANEL diverge from PALLET tuning.
 */

import { orientationCapacity } from "./types";
import { chooseOrientation } from "./orientation";

export const DEFAULT_MIN_BELOW_FRACTION = 0.3;

/** If stepped-to-floor geometry cannot reach this fill fraction, lift the line. */
export const FILL_LIFT_THRESHOLD = 0.4;

/** Filler line from top-right of peak plateau → container floor at length end. */
export const DIAGONAL_PEAK_RIGHT_TO_END = "peakRightToContainerEnd";
/** Legacy Packly/ContainerFiller diagonal. */
export const DIAGONAL_LEGACY = "legacy";

/** Shortest allowed column height for a given parallel lift of the filler line. */
export function minLastHeightFromLift(floorLift) {
  return Math.max(1, Math.floor(floorLift) + 1);
}

/**
 * Max column-slots of a minStepWidth staircase from ny down to minLastHeight.
 */
function maxStaircaseSlots(nz, ny, minStepWidth, floorLift) {
  const w = Math.max(1, minStepWidth);
  const minH = minLastHeightFromLift(floorLift);
  if (minH > ny || nz <= 0) return 0;
  let best = 0;
  for (let peak = minH; peak <= ny; peak++) {
    const levels = peak - minH + 1;
    const colsNeeded = levels * w;
    if (colsNeeded > nz) continue;
    const slots = (w * levels * (minH + peak)) / 2;
    if (slots > best) best = slots;
  }
  return Math.min(best, nz * ny);
}

/**
 * Raise the virtual floor (parallel filler-line lift) until the stepped profile
 * can hold `slots`. Over-lifting shrinks capacity, so pick the smallest viable
 * lift (or the lift with max staircase capacity if slots cannot fully fit).
 */
function chooseFloorLift(slots, nz, ny, minStepWidth) {
  const grid = Math.max(1, nz * ny);
  const maxLift = Math.max(0, ny - 1);

  let bestLift = 0;
  let bestMaxS = maxStaircaseSlots(nz, ny, minStepWidth, 0);
  let fitLift = null;

  for (let lift = 0; lift <= maxLift; lift++) {
    const maxS = maxStaircaseSlots(nz, ny, minStepWidth, lift);
    if (maxS > bestMaxS) {
      bestMaxS = maxS;
      bestLift = lift;
    }
    if (fitLift == null && maxS >= slots) {
      fitLift = lift;
    }
  }

  if (fitLift == null) {
    return bestLift;
  }

  // Quantity aims for ≥40% of the container: keep lifting while it still
  // increases (or preserves) capacity toward the threshold.
  let lift = fitLift;
  if (slots / grid >= FILL_LIFT_THRESHOLD) {
    while (lift < maxLift) {
      const curS = maxStaircaseSlots(nz, ny, minStepWidth, lift);
      if (curS / grid >= FILL_LIFT_THRESHOLD) break;
      const nextS = maxStaircaseSlots(nz, ny, minStepWidth, lift + 1);
      if (nextS < curS) break;
      lift += 1;
    }
  }
  return lift;
}

function diagonalY(z, z0, y0, z1, y1) {
  if (Math.abs(z1 - z0) < 1e-12) return y0;
  const t = (z - z0) / (z1 - z0);
  return y0 + t * (y1 - y0);
}

function boxBelowFraction(y0, y1, zMid, z0Line, y0Line, z1Line, y1Line) {
  const yLine = diagonalY(zMid, z0Line, y0Line, z1Line, y1Line);
  const h = y1 - y0;
  if (h <= 0) return 0;
  if (yLine <= y0) return 0;
  if (yLine >= y1) return 1;
  return (yLine - y0) / h;
}

function maxHeightForColumn(
  col,
  ny,
  boxH,
  boxD,
  z0Line,
  y0Line,
  z1Line,
  y1Line,
  minBelow
) {
  const zMid = (col + 0.5) * boxD;
  let allowed = 0;
  for (let level = 0; level < ny; level++) {
    const y0 = level * boxH;
    const y1 = (level + 1) * boxH;
    const below = boxBelowFraction(y0, y1, zMid, z0Line, y0Line, z1Line, y1Line);
    if (below >= minBelow) {
      allowed = level + 1;
    } else {
      break;
    }
  }
  return allowed;
}

/**
 * Non-increasing front→back, adjacent drop ≤ 1 (including shortest column →
 * elevated floor at minLastHeight-1), each positive plateau ≥ minStepWidth.
 */
function validStep(
  heights,
  minStepWidth = 1,
  allowShortTail = true,
  minLastHeight = 1
) {
  const minLast = Math.max(1, minLastHeight);
  for (let i = 0; i < heights.length - 1; i++) {
    if (heights[i] < heights[i + 1]) return false;
    if (heights[i] - heights[i + 1] > 1) return false;
  }

  // Unit step from the shortest column down to the (possibly elevated) floor.
  let lastPositive = -1;
  for (let i = heights.length - 1; i >= 0; i--) {
    if (heights[i] > 0) {
      lastPositive = i;
      break;
    }
  }
  if (lastPositive >= 0 && heights[lastPositive] !== minLast) {
    return false;
  }

  if (minStepWidth <= 1) return true;

  let i = 0;
  const n = heights.length;
  const plateaus = [];
  while (i < n) {
    const h = heights[i];
    let j = i + 1;
    while (j < n && heights[j] === h) j += 1;
    if (h > 0) plateaus.push({ start: i, width: j - i, h });
    i = j;
  }
  for (let p = 0; p < plateaus.length; p++) {
    const isLast = p === plateaus.length - 1;
    if (plateaus[p].width < minStepWidth && !(allowShortTail && isLast)) {
      return false;
    }
  }
  return true;
}

/**
 * Lower the back until the last positive column equals minLastHeight.
 */
function enforceStepDownToFloor(heights, minLastHeight = 1) {
  const minLast = Math.max(1, minLastHeight);
  const out = heights.slice();
  let guard = 0;
  while (guard++ < out.length * (Math.max(0, ...out) + 1) + 5) {
    let last = -1;
    for (let i = out.length - 1; i >= 0; i--) {
      if (out[i] > 0) {
        last = i;
        break;
      }
    }
    if (last < 0 || out[last] <= minLast) break;

    out[last] -= 1;
    for (let i = 1; i < out.length; i++) {
      if (out[i] > out[i - 1]) out[i] = out[i - 1];
    }
    for (let i = out.length - 2; i >= 0; i--) {
      if (out[i] > out[i + 1] + 1) out[i] = out[i + 1] + 1;
    }
  }
  return out;
}

function enforceStepHeight(heights, ny, minStepWidth = 1, minLastHeight = 1) {
  if (!heights.length) return heights;
  const minLast = Math.max(1, minLastHeight);
  let out = heights.map((h) => Math.max(0, Math.min(ny, h)));

  for (let i = 1; i < out.length; i++) {
    if (out[i] > out[i - 1]) out[i] = out[i - 1];
  }
  for (let i = out.length - 2; i >= 0; i--) {
    if (out[i] > out[i + 1] + 1) out[i] = out[i + 1] + 1;
  }

  out = enforceStepDownToFloor(out, minLast);

  if (minStepWidth <= 1) return out;

  let changed = true;
  while (changed) {
    changed = false;
    let i = 0;
    while (i < out.length) {
      const h = out[i];
      if (h <= 0) {
        i += 1;
        continue;
      }
      let j = i + 1;
      while (j < out.length && out[j] === h) j += 1;
      const width = j - i;
      const isLastPositive =
        !out.slice(j).some((v) => v > 0) || j >= out.length;
      if (width < minStepWidth && !isLastPositive) {
        const nextH = j < out.length ? out[j] : Math.max(minLast, h - 1);
        for (let k = i; k < j; k++) out[k] = nextH;
        changed = true;
        break;
      }
      i = j;
    }
    for (let k = 1; k < out.length; k++) {
      if (out[k] > out[k - 1]) out[k] = out[k - 1];
    }
    for (let k = out.length - 2; k >= 0; k--) {
      if (out[k] > out[k + 1] + 1) out[k] = out[k + 1] + 1;
    }
    out = enforceStepDownToFloor(out, minLast);
  }
  return out;
}

function columnHeightsForDiagonal(
  nz,
  ny,
  boxH,
  boxD,
  z0,
  y0,
  z1,
  y1,
  fullThrough,
  minBelow,
  minStepWidth,
  minLastHeight = 1
) {
  const heights = [];
  for (let col = 0; col < nz; col++) {
    if (col <= fullThrough) {
      heights.push(ny);
    } else {
      heights.push(
        maxHeightForColumn(col, ny, boxH, boxD, z0, y0, z1, y1, minBelow)
      );
    }
  }
  return enforceStepHeight(heights, ny, minStepWidth, minLastHeight);
}

/**
 * Even length-first profile over nCols columns: spread slots across the floor
 * before stacking higher. Front may be one level taller when rem > 0.
 * @returns {number[]|null}
 */
function buildEvenStepProfile(nCols, slots, ny, minStepWidth, minLastHeight = 1) {
  if (nCols <= 0 || slots <= 0 || nCols * ny < slots) return null;
  const w = Math.max(1, minStepWidth);
  const minLast = Math.max(1, minLastHeight);
  const base = Math.floor(slots / nCols);
  const rem = slots % nCols;
  // Rear height is always `base` (front holds the remainder) → must match elevated floor.
  if (base !== minLast) return null;
  if (base > ny || (rem > 0 && base + 1 > ny)) return null;
  if (rem > 0 && rem < w) return null;
  const rear = nCols - rem;
  if (base > 0 && rem > 0 && rear > 0 && rear < w) return null;

  const profile = [];
  for (let i = 0; i < nCols; i++) {
    profile.push(i < rem ? base + 1 : base);
  }
  if (!validStep(profile, w, true, minLast)) return null;
  return profile;
}

/**
 * Length-first: maximize columns used (spread over floor), minimize peak height.
 */
function staircaseHeightsLengthFirst(
  slots,
  nz,
  ny,
  fullThrough,
  minStepWidth,
  minLastHeight = 1
) {
  const slotCount = Math.max(0, Math.min(slots, nz * ny));
  let heights = Array(nz).fill(0);
  fullThrough = Math.max(-1, Math.min(nz - 1, fullThrough));
  const w = Math.max(1, minStepWidth);
  const minLast = Math.max(1, minLastHeight);

  for (let c = 0; c <= fullThrough; c++) {
    heights[c] = ny;
  }

  let placed = heights.reduce((a, b) => a + b, 0);
  if (placed >= slotCount) {
    while (heights.reduce((a, b) => a + b, 0) > slotCount) {
      for (let c = nz - 1; c >= 0; c--) {
        if (heights[c] > 0) {
          heights[c] -= 1;
          break;
        }
      }
    }
    return enforceStepHeight(heights, ny, w, minLast);
  }

  const remaining = slotCount - placed;
  const start = fullThrough + 1;
  const availCols = nz - start;
  if (availCols <= 0 || remaining <= 0) {
    return enforceStepHeight(heights, ny, w, minLast);
  }

  let profile = null;
  for (let nCols = availCols; nCols >= 1; nCols--) {
    profile = buildEvenStepProfile(nCols, remaining, ny, w, minLast);
    if (profile) break;
  }

  if (!profile) {
    profile = Array(availCols).fill(0);
    let left = remaining;
    let h = minLast;
    while (left > 0 && h <= ny) {
      for (let i = 0; i < availCols && left > 0; i++) {
        if (profile[i] < h && profile[i] < ny) {
          if (i === 0 || profile[i - 1] >= profile[i] + 1) {
            profile[i] += 1;
            left -= 1;
          }
        }
      }
      h += 1;
      if (h > ny + 1) break;
    }
  }

  for (let i = 0; i < profile.length; i++) {
    if (start + i < nz) heights[start + i] = profile[i];
  }

  while (heights.reduce((a, b) => a + b, 0) < slotCount) {
    let added = false;
    for (let c = 0; c < nz - 1; c++) {
      if (heights[c] <= 0) continue;
      if (heights[c + 1] >= heights[c]) continue;
      if (heights[c + 1] !== heights[c] - 1 && heights[c + 1] !== 0) continue;
      const trial = heights.slice();
      trial[c + 1] = heights[c];
      if (trial[c + 1] > ny) continue;
      if (validStep(trial, w, true, minLast)) {
        heights = trial;
        added = true;
        break;
      }
    }
    if (added) continue;

    let bestC = -1;
    let bestH = Infinity;
    for (let c = 0; c < nz; c++) {
      if (heights[c] >= ny) continue;
      if (heights[c] < bestH) {
        bestH = heights[c];
        bestC = c;
      }
    }
    if (bestC >= 0) {
      const trial = heights.slice();
      trial[bestC] += 1;
      if (validStep(trial, w, true, minLast)) {
        heights = trial;
        added = true;
      }
    }
    if (!added) break;
  }

  return enforceStepHeight(heights, ny, w, minLast);
}

/**
 * Compact front staircase (height-first) — used when preferLength is false.
 * Ramps from peak down to minLastHeight (elevated floor when line is lifted).
 */
function staircaseHeightsHeightFirst(
  slots,
  nz,
  ny,
  fullThrough,
  minStepWidth,
  minLastHeight = 1
) {
  const slotCount = Math.max(0, Math.min(slots, nz * ny));
  let heights = Array(nz).fill(0);
  fullThrough = Math.max(-1, Math.min(nz - 1, fullThrough));
  const w = Math.max(1, minStepWidth);
  const minLast = Math.max(1, Math.min(ny, minLastHeight));

  for (let c = 0; c <= fullThrough; c++) {
    heights[c] = ny;
  }

  let placed = heights.reduce((a, b) => a + b, 0);
  if (placed >= slotCount) {
    while (heights.reduce((a, b) => a + b, 0) > slotCount) {
      for (let c = nz - 1; c >= 0; c--) {
        if (heights[c] > 0) {
          heights[c] -= 1;
          break;
        }
      }
    }
    return enforceStepHeight(heights, ny, w, minLast);
  }

  const remaining = slotCount - placed;
  const start = fullThrough + 1;
  const availCols = nz - start;

  let peak = fullThrough < 0 ? Math.min(ny, Math.max(minLast, remaining)) : ny;
  for (let p = minLast; p <= ny; p++) {
    const levels = p - minLast + 1;
    const colsNeeded = levels * w;
    const slotsNeeded = (w * levels * (minLast + p)) / 2;
    if (
      (colsNeeded <= availCols && slotsNeeded >= remaining) ||
      p === ny
    ) {
      peak = p;
      if (colsNeeded <= availCols && slotsNeeded >= remaining) break;
    }
  }

  const ramp = Array(Math.max(0, availCols)).fill(0);
  let col = 0;
  for (let h = peak; h >= minLast && col < ramp.length; h--) {
    for (let k = 0; k < w && col < ramp.length; k++) {
      ramp[col++] = h;
    }
  }

  while (ramp.reduce((a, b) => a + b, 0) > remaining) {
    let trimmed = false;
    for (let i = ramp.length - 1; i >= 0; i--) {
      if (ramp[i] <= 0) continue;
      // Never trim below elevated floor on the trailing edge.
      if (ramp[i] <= minLast) continue;
      ramp[i] -= 1;
      const full = Array(nz).fill(0);
      for (let c = 0; c <= fullThrough; c++) full[c] = ny;
      for (let i2 = 0; i2 < ramp.length; i2++) {
        if (start + i2 < nz) full[start + i2] = ramp[i2];
      }
      if (validStep(full, w, true, minLast)) {
        trimmed = true;
        break;
      }
      ramp[i] += 1;
    }
    if (!trimmed) break;
  }

  for (let i = 0; i < ramp.length; i++) {
    if (start + i < nz) heights[start + i] = ramp[i];
  }

  while (heights.reduce((a, b) => a + b, 0) < slotCount) {
    let added = false;
    // Prefer adding on top of the tallest front plateau (lift fill).
    for (let c = 0; c < nz; c++) {
      if (heights[c] <= 0 || heights[c] >= ny) continue;
      if (c > 0 && heights[c] >= heights[c - 1]) continue;
      const trial = heights.slice();
      trial[c] += 1;
      if (validStep(trial, w, true, minLast)) {
        heights = trial;
        added = true;
        break;
      }
    }
    if (added) continue;

    for (let c = 0; c < nz - 1; c++) {
      if (heights[c] <= 0) continue;
      if (heights[c + 1] >= heights[c]) continue;
      if (heights[c + 1] !== heights[c] - 1 && heights[c + 1] !== 0) continue;
      const trial = heights.slice();
      trial[c + 1] = heights[c];
      if (trial[c + 1] > ny) continue;
      if (validStep(trial, w, true, minLast)) {
        heights = trial;
        added = true;
        break;
      }
    }
    if (!added) break;
  }

  return enforceStepHeight(heights, ny, w, minLast);
}

function staircaseHeights(
  slots,
  nz,
  ny,
  fullThrough = -1,
  minStepWidth = 1,
  preferLength = false,
  minLastHeight = 1
) {
  if (preferLength) {
    return staircaseHeightsLengthFirst(
      slots,
      nz,
      ny,
      fullThrough,
      minStepWidth,
      minLastHeight
    );
  }
  return staircaseHeightsHeightFirst(
    slots,
    nz,
    ny,
    fullThrough,
    minStepWidth,
    minLastHeight
  );
}

/**
 * Filler line: peak-right → right wall at elevated floor (floorLift * boxH).
 * Same slope as to true floor; parallel lift moves the line up by master height.
 */
function peakRightDiagonalAnchors(heights, boxH, boxD, contL, floorLift = 0) {
  const yFloor = Math.max(0, floorLift) * boxH;
  if (!heights.length || Math.max(...heights) <= 0) {
    return { z0: 0, y0: yFloor, z1: contL, y1: yFloor };
  }
  const peak = Math.max(...heights);
  let rightmostPeak = 0;
  for (let c = 0; c < heights.length; c++) {
    if (heights[c] === peak) rightmostPeak = c;
  }
  return {
    z0: (rightmostPeak + 1) * boxD,
    y0: peak * boxH,
    z1: contL,
    y1: yFloor,
  };
}

function diagonalPeakRightToContainerEnd(
  heights,
  boxH,
  boxD,
  contL,
  floorLift = 0
) {
  const { z0, y0, z1, y1 } = peakRightDiagonalAnchors(
    heights,
    boxH,
    boxD,
    contL,
    floorLift
  );
  return [z0, y0, z1, y1];
}

/**
 * Cross-section area under the filler line that is not occupied by stacks.
 * Line: peak-right → container lower-right; flat at peak height before z0.
 */
function unoccupiedAreaBelowDiagonal(
  heights,
  boxH,
  boxD,
  contL,
  floorLift = 0
) {
  const { z0, y0, z1, y1 } = peakRightDiagonalAnchors(
    heights,
    boxH,
    boxD,
    contL,
    floorLift
  );
  let area = 0;
  const n = heights.length;

  for (let c = 0; c < n; c++) {
    const zA = c * boxD;
    const zB = Math.min((c + 1) * boxD, contL);
    if (zB <= zA) continue;
    const zMid = 0.5 * (zA + zB);
    const yLine =
      zMid <= z0 ? y0 : Math.max(y1, diagonalY(zMid, z0, y0, z1, y1));
    const occ = heights[c] * boxH;
    area += Math.max(0, yLine - occ) * (zB - zA);
  }

  const zGridEnd = n * boxD;
  if (zGridEnd < contL) {
    if (zGridEnd < z0) {
      area += Math.max(0, y0 - y1) * (Math.min(z0, contL) - zGridEnd);
      if (z0 < contL) {
        area += 0.5 * Math.max(0, y0 - y1) * (contL - z0);
      }
    } else {
      const yA = Math.max(y1, diagonalY(zGridEnd, z0, y0, z1, y1));
      area += 0.5 * (yA + y1) * (contL - zGridEnd);
    }
  }
  return area;
}

function profileRespectsDiagonal(
  heights,
  ny,
  boxH,
  boxD,
  contL,
  minBelow,
  minStepWidth,
  floorLift = 0,
  minLastHeight = 1
) {
  const peak = Math.max(0, ...heights);
  let rightmostPeak = -1;
  for (let c = 0; c < heights.length; c++) {
    if (heights[c] === peak) rightmostPeak = c;
  }
  const { z0, y0, z1, y1 } = peakRightDiagonalAnchors(
    heights,
    boxH,
    boxD,
    contL,
    floorLift
  );
  const allowed = columnHeightsForDiagonal(
    heights.length,
    ny,
    boxH,
    boxD,
    z0,
    y0,
    z1,
    y1,
    rightmostPeak,
    minBelow,
    minStepWidth,
    minLastHeight
  );
  for (let c = 0; c < heights.length; c++) {
    if (heights[c] > allowed[c]) return false;
  }
  return true;
}

/**
 * Reshape a fixed-slot height-first profile to minimize empty area under the
 * filler line (diagonal recomputed from each candidate).
 */
function minimizeUnoccupiedUnderDiagonal(
  heightsIn,
  slots,
  ny,
  boxH,
  boxD,
  contL,
  minBelow,
  minStepWidth,
  floorLift = 0
) {
  const w = Math.max(1, minStepWidth);
  const minLast = minLastHeightFromLift(floorLift);
  let heights = heightsIn.slice();

  while (heights.reduce((a, b) => a + b, 0) > slots) {
    let trimmed = false;
    for (let c = heights.length - 1; c >= 0; c--) {
      if (heights[c] <= 0) continue;
      if (heights[c] <= minLast) continue;
      heights[c] -= 1;
      if (validStep(heights, w, true, minLast)) {
        trimmed = true;
        break;
      }
      heights[c] += 1;
    }
    if (!trimmed) break;
  }

  heights = enforceStepHeight(heights, ny, w, minLast);
  let best = heights.slice();
  let bestScore = unoccupiedAreaBelowDiagonal(
    best,
    boxH,
    boxD,
    contL,
    floorLift
  );
  const nz = best.length;
  let improved = true;
  let guard = 0;

  while (improved && guard < 500) {
    improved = false;
    guard += 1;
    for (let lose = 0; lose < nz; lose++) {
      if (best[lose] <= 0) continue;
      for (let gain = 0; gain < nz; gain++) {
        if (gain === lose) continue;
        if (best[gain] >= ny) continue;
        const trial = best.slice();
        trial[lose] -= 1;
        trial[gain] += 1;
        if (!validStep(trial, w, true, minLast)) continue;
        if (
          !profileRespectsDiagonal(
            trial,
            ny,
            boxH,
            boxD,
            contL,
            minBelow,
            w,
            floorLift,
            minLast
          )
        ) {
          continue;
        }
        const score = unoccupiedAreaBelowDiagonal(
          trial,
          boxH,
          boxD,
          contL,
          floorLift
        );
        if (score + 1e-9 < bestScore) {
          best = trial;
          bestScore = score;
          improved = true;
        }
      }
    }
  }

  return enforceStepHeight(best, ny, w, minLast);
}

function diagonalForHeightsLegacy(heights, boxH, boxD, ny, fillFrac) {
  if (!heights.length || Math.max(...heights) <= 0) {
    return [0, 0, boxD, 0];
  }

  let fullTo = -1;
  for (let c = 0; c < heights.length; c++) {
    if (heights[c] >= ny) fullTo = c;
    else break;
  }

  let active = 0;
  for (let i = heights.length - 1; i >= 0; i--) {
    if (heights[i] > 0) {
      active = i;
      break;
    }
  }

  let z0;
  let y0;
  if (fullTo >= 0 && fillFrac > 0.5) {
    z0 = (fullTo + 1) * boxD;
    y0 = ny * boxH;
  } else {
    z0 = 0;
    y0 = heights[0] * boxH;
  }
  const z1 = (active + 1) * boxD;
  return [z0, y0, z1, 0];
}

function widenPlateausWithDiagonal(
  heights,
  ny,
  boxH,
  boxD,
  minBelow,
  minStepWidth,
  contL,
  diagonalMode,
  floorLift = 0
) {
  if (Math.max(...heights) <= 0) return heights;
  const minLast = minLastHeightFromLift(floorLift);
  const peak = Math.max(...heights);
  let rightmostPeak = 0;
  for (let c = 0; c < heights.length; c++) {
    if (heights[c] === peak) rightmostPeak = c;
  }

  let z0;
  let y0;
  let z1;
  let y1;
  if (diagonalMode === DIAGONAL_PEAK_RIGHT_TO_END) {
    ({ z0, y0, z1, y1 } = peakRightDiagonalAnchors(
      heights,
      boxH,
      boxD,
      contL,
      floorLift
    ));
  } else {
    let active = 0;
    for (let i = heights.length - 1; i >= 0; i--) {
      if (heights[i] > 0) {
        active = i;
        break;
      }
    }
    z0 = 0;
    y0 = heights[0] * boxH;
    z1 = (active + 1) * boxD;
    y1 = floorLift * boxH;
  }

  const allowed = columnHeightsForDiagonal(
    heights.length,
    ny,
    boxH,
    boxD,
    z0,
    y0,
    z1,
    y1,
    -1,
    minBelow,
    minStepWidth,
    minLast
  );
  let out = heights.slice();
  let changed = true;
  while (changed) {
    changed = false;
    for (let c = 0; c < out.length - 1; c++) {
      if (out[c] <= 0) continue;
      if (out[c + 1] === out[c]) continue;
      if (out[c + 1] === out[c] - 1 && allowed[c + 1] >= out[c]) {
        const trial = out.slice();
        trial[c + 1] = out[c];
        if (
          validStep(trial, minStepWidth, true, minLast) &&
          trial[c + 1] <= allowed[c + 1]
        ) {
          out = trial;
          changed = true;
          break;
        }
      }
    }
  }
  return enforceStepHeight(out, ny, minStepWidth, minLast);
}

/**
 * @param {number[]} heights
 * @param {number} nx
 * @param {number} quantity
 * @param {number} boxW
 * @param {number} boxH
 * @param {number} boxD
 * @param {"master"|"pallet"} kind
 * @param {number} [zOffset]
 * @param {number} [containerIndex]
 */
export function placeBoxes(
  heights,
  nx,
  quantity,
  boxW,
  boxH,
  boxD,
  kind = "master",
  zOffset = 0,
  containerIndex = 0
) {
  const boxes = [];
  const actual = Array(heights.length).fill(0);
  let remaining = quantity;
  for (let col = 0; col < heights.length; col++) {
    const maxH = heights[col];
    if (remaining <= 0) break;
    for (let level = 0; level < maxH; level++) {
      if (remaining <= 0) break;
      for (let row = 0; row < nx; row++) {
        if (remaining <= 0) break;
        boxes.push({
          x: row * boxW,
          y: level * boxH,
          z: zOffset + col * boxD,
          w: boxW,
          h: boxH,
          d: boxD,
          col,
          level,
          row,
          kind,
          containerIndex,
        });
        remaining -= 1;
        actual[col] = level + 1;
      }
    }
  }
  return { boxes, actualHeights: actual };
}

function stepsFromHeights(heights) {
  const steps = [];
  let i = 0;
  const n = heights.length;
  while (i < n) {
    const h = heights[i];
    if (h <= 0) {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < n && heights[j] === h) j += 1;
    steps.push({ startCol: i, numCols: j - i, heightBoxes: h });
    i = j;
  }
  return steps;
}

/**
 * Pack quantity of uniform units into one container region along Z.
 */
export function packSingleContainer({
  containerIndex,
  contL,
  contW,
  contH,
  orientation: o,
  quantity,
  kind = "master",
  zOffset = 0,
  usableLength = null,
  minBelowFraction = DEFAULT_MIN_BELOW_FRACTION,
  minStepWidth = 1,
  diagonalMode = DIAGONAL_LEGACY,
  preferLength = false,
  minimizeUnoccupied = false,
}) {
  const capacity = orientationCapacity(o);
  const qty = Math.max(0, Math.min(quantity, capacity));
  const stepW = Math.max(1, minStepWidth);

  const empty = {
    containerIndex,
    contL,
    contW,
    contH,
    orientation: o,
    quantity: 0,
    capacity,
    boxes: [],
    columnHeights: Array(o.nz).fill(0),
    steps: [],
    diagonal: [0, 0, contL, 0],
  };

  if (qty === 0 || o.nx === 0 || o.ny === 0 || o.nz === 0) {
    return empty;
  }

  const fillFrac = capacity ? qty / capacity : 0;
  const fullLevels = Math.floor(qty / o.nx);
  const remBoxes = qty % o.nx;
  const slots = fullLevels + (remBoxes ? 1 : 0);

  // If stepped-to-true-floor cannot hold the load / stay under ~40% geometry,
  // lift the filler line in parallel (+1 master height per lift) so we can
  // stack on top of the tallest columns with the same slope.
  const useLift =
    diagonalMode === DIAGONAL_PEAK_RIGHT_TO_END && stepW >= 2;
  const floorLift = useLift
    ? chooseFloorLift(slots, o.nz, o.ny, stepW)
    : 0;
  const minLast = minLastHeightFromLift(floorLift);

  let heights;
  if (fillFrac <= 0.5) {
    heights = staircaseHeights(
      slots,
      o.nz,
      o.ny,
      -1,
      stepW,
      preferLength,
      minLast
    );
    heights = widenPlateausWithDiagonal(
      heights,
      o.ny,
      o.boxH,
      o.boxD,
      minBelowFraction,
      stepW,
      contL,
      diagonalMode,
      floorLift
    );
  } else {
    heights = null;
    for (let fullThrough = -1; fullThrough < o.nz; fullThrough++) {
      let candidate = staircaseHeights(
        slots,
        o.nz,
        o.ny,
        fullThrough,
        stepW,
        preferLength,
        minLast
      );
      candidate = widenPlateausWithDiagonal(
        candidate,
        o.ny,
        o.boxH,
        o.boxD,
        minBelowFraction,
        stepW,
        contL,
        diagonalMode,
        floorLift
      );
      heights = candidate;
      if (candidate.reduce((a, b) => a + b, 0) >= slots) break;
    }
  }

  while (heights.reduce((a, b) => a + b, 0) > slots) {
    let trimmed = false;
    for (let c = o.nz - 1; c >= 0; c--) {
      if (heights[c] <= 0) continue;
      if (heights[c] <= minLast) continue;
      heights[c] -= 1;
      if (validStep(heights, stepW, true, minLast)) {
        trimmed = true;
        break;
      }
      heights[c] += 1;
    }
    if (!trimmed) break;
  }

  heights = enforceStepHeight(heights, o.ny, stepW, minLast);

  // After lifts: keep adding on the tallest front columns under the elevated
  // parallel line until slots are met (same slope, line moved up).
  while (heights.reduce((a, b) => a + b, 0) < slots) {
    let added = false;
    const peak = Math.max(0, ...heights);
    for (let c = 0; c < o.nz; c++) {
      if (heights[c] !== peak) continue;
      if (heights[c] >= o.ny) continue;
      const trial = heights.slice();
      trial[c] += 1;
      if (!validStep(trial, stepW, true, minLast)) continue;
      if (
        !profileRespectsDiagonal(
          trial,
          o.ny,
          o.boxH,
          o.boxD,
          contL,
          minBelowFraction,
          stepW,
          floorLift,
          minLast
        )
      ) {
        continue;
      }
      heights = trial;
      added = true;
      break;
    }
    if (!added) break;
  }

  if (
    minimizeUnoccupied &&
    diagonalMode === DIAGONAL_PEAK_RIGHT_TO_END
  ) {
    heights = minimizeUnoccupiedUnderDiagonal(
      heights,
      slots,
      o.ny,
      o.boxH,
      o.boxD,
      contL,
      minBelowFraction,
      stepW,
      floorLift
    );
  }

  const { boxes, actualHeights } = placeBoxes(
    heights,
    o.nx,
    qty,
    o.boxW,
    o.boxH,
    o.boxD,
    kind,
    zOffset,
    containerIndex
  );

  const diag =
    diagonalMode === DIAGONAL_PEAK_RIGHT_TO_END
      ? diagonalPeakRightToContainerEnd(
          actualHeights,
          o.boxH,
          o.boxD,
          contL,
          floorLift
        )
      : diagonalForHeightsLegacy(
          actualHeights,
          o.boxH,
          o.boxD,
          o.ny,
          fillFrac
        );

  void usableLength;

  return {
    containerIndex,
    contL,
    contW,
    contH,
    orientation: o,
    quantity: boxes.length,
    capacity,
    boxes,
    columnHeights: actualHeights,
    steps: stepsFromHeights(actualHeights),
    diagonal: diag,
  };
}

/**
 * Pack quantity of uniform cartons across one or more containers.
 * @param {object} [options]
 * @param {number} [options.minBelowFraction]
 * @param {number} [options.minStepWidth]
 * @param {string} [options.diagonalMode]
 * @param {boolean} [options.preferLength] - spread along length before stacking height
 * @param {boolean} [options.minimizeUnoccupied] - reshape to hug filler line
 */
export function packAll(
  cartonW,
  cartonH,
  cartonD,
  quantity,
  containerDims,
  kind = "master",
  minBelowFractionOrOptions = DEFAULT_MIN_BELOW_FRACTION,
  maybeOptions
) {
  // Support legacy signature packAll(..., kind, minBelow) and options object.
  let minBelowFraction = DEFAULT_MIN_BELOW_FRACTION;
  let minStepWidth = 1;
  let diagonalMode = DIAGONAL_LEGACY;
  let preferLength = false;
  let minimizeUnoccupied = false;

  if (
    minBelowFractionOrOptions &&
    typeof minBelowFractionOrOptions === "object"
  ) {
    minBelowFraction =
      minBelowFractionOrOptions.minBelowFraction ?? DEFAULT_MIN_BELOW_FRACTION;
    minStepWidth = minBelowFractionOrOptions.minStepWidth ?? 1;
    diagonalMode =
      minBelowFractionOrOptions.diagonalMode ?? DIAGONAL_LEGACY;
    preferLength = Boolean(minBelowFractionOrOptions.preferLength);
    minimizeUnoccupied = Boolean(
      minBelowFractionOrOptions.minimizeUnoccupied
    );
  } else {
    minBelowFraction = minBelowFractionOrOptions;
    if (maybeOptions && typeof maybeOptions === "object") {
      minStepWidth = maybeOptions.minStepWidth ?? 1;
      diagonalMode = maybeOptions.diagonalMode ?? DIAGONAL_LEGACY;
      minBelowFraction =
        maybeOptions.minBelowFraction ?? minBelowFraction;
      preferLength = Boolean(maybeOptions.preferLength);
      minimizeUnoccupied = Boolean(maybeOptions.minimizeUnoccupied);
    }
  }

  quantity = Math.max(0, Math.floor(quantity));
  if (!containerDims?.length) {
    throw new Error("At least one container dimension tuple is required");
  }

  const containers = [];
  let remaining = quantity;
  let idx = 0;
  const dimsList = containerDims.map((d) => [...d]);

  while (remaining > 0 || idx === 0) {
    if (idx >= dimsList.length) {
      dimsList.push([...dimsList[dimsList.length - 1]]);
    }
    const [contL, contW, contH] = dimsList[idx];
    const orientation = chooseOrientation(
      cartonW,
      cartonH,
      cartonD,
      contL,
      contW,
      contH
    );
    const cap = orientationCapacity(orientation);

    if (cap <= 0) {
      containers.push({
        containerIndex: idx,
        contL,
        contW,
        contH,
        orientation,
        quantity: 0,
        capacity: 0,
        boxes: [],
        columnHeights: [],
        steps: [],
        diagonal: [0, 0, contL, 0],
      });
      break;
    }

    const take = remaining > 0 ? Math.min(remaining, cap) : 0;
    const result = packSingleContainer({
      containerIndex: idx,
      contL,
      contW,
      contH,
      orientation,
      quantity: take,
      kind,
      minBelowFraction,
      minStepWidth,
      diagonalMode,
      preferLength,
      minimizeUnoccupied,
    });
    containers.push(result);
    remaining -= result.quantity;
    idx += 1;
    if (remaining <= 0) break;
    if (idx > 100) break;
  }

  const primary =
    containers[0]?.orientation ??
    chooseOrientation(cartonW, cartonH, cartonD, ...containerDims[0]);
  const totalCapacity = containers.reduce((s, c) => s + c.capacity, 0);

  return {
    orientation: primary,
    containers,
    totalQuantity: quantity,
    totalCapacity,
  };
}

/**
 * Max Z extent occupied by placed boxes (min corner + depth).
 * @param {import("./types").PlacedBox[]} boxes
 */
export function maxZExtent(boxes) {
  if (!boxes.length) return 0;
  return Math.max(...boxes.map((b) => b.z + b.d));
}
